"use client"

import { useState, useEffect } from "react"
import "./Register.css"
import { Link } from "react-router-dom"
import axios from "axios"
import { useNavigate, useLocation } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { User, Mail, Lock, Building, FileText, MapPin, Hash, Globe, CheckCircle, AlertCircle, Loader } from 'lucide-react'
import Logo from "../img/logo3.png"

const GymRegistration = () => {
  const [isLoginMode, setIsLoginMode] = useState(false)
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  })
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    gymName: "",
    gymDescription: "",
    gymLocation: {
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pinCode: "",
      country: "India",
    },
  })

  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [modalMessage, setModalMessage] = useState("")
  const [modalType, setModalType] = useState("info") // 'success', 'error', 'info'
  const [loading, setLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const [errors, setErrors] = useState([])
  const [fieldErrors, setFieldErrors] = useState({})

  // Fetch states when the component mounts
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.post("https://countriesnow.space/api/v0.1/countries/states", {
          country: "India",
        })
        setStates(response.data.data.states)
      } catch (error) {
        console.error("Error fetching states:", error)
      }
    }
    fetchStates()
  }, [])

  // Fetch cities based on the selected state
  const fetchCities = async (state) => {
    try {
      const response = await axios.post("https://countriesnow.space/api/v0.1/countries/state/cities", {
        country: "India",
        state,
      })
      setCities(response.data.data)
    } catch (error) {
      console.error("Error fetching cities:", error)
    }
  }

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: "" })
    }
  }

  const handleLoginChange = (e) => {
    const { name, value } = e.target
    setLoginData({ ...loginData, [name]: value })
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: "" })
    }
  }

  // Handle location input changes
  const handleGymLocationChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, gymLocation: { ...formData.gymLocation, [name]: value } })

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: "" })
    }

    if (name === "state") {
      fetchCities(value)
      // Reset city when state changes
      setFormData({
        ...formData,
        gymLocation: { ...formData.gymLocation, [name]: value, city: "" },
      })
    }
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}
    const { email, password, gymName, gymDescription, gymLocation } = formData

    if (!email) newErrors.email = "Email is required"
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Email is invalid"

    if (!password) newErrors.password = "Password is required"
    else if (password.length < 6) newErrors.password = "Password must be at least 6 characters"

    if (!gymName) newErrors.gymName = "Gym name is required"
    if (!gymDescription) newErrors.gymDescription = "Gym description is required"
    if (!gymLocation.addressLine1) newErrors.addressLine1 = "Address is required"
    if (!gymLocation.pinCode) newErrors.pinCode = "Pin code is required"
    else if (!/^\d{6}$/.test(gymLocation.pinCode)) newErrors.pinCode = "Pin code must be 6 digits"
    if (!gymLocation.state) newErrors.state = "State is required"
    if (!gymLocation.city) newErrors.city = "City is required"

    setFieldErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleLogin = async (e) => {
    e.preventDefault()

    const newErrors = {}
    if (!loginData.email) newErrors.email = "Email is required"
    else if (!/\S+@\S+\.\S+/.test(loginData.email)) newErrors.email = "Email is invalid"
    if (!loginData.password) newErrors.password = "Password is required"

    setFieldErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form")
      return
    }

    setFormLoading(true)

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/login`, loginData)

      if (response.status === 200) {
        setModalMessage("Login successful! Redirecting to dashboard...")
        setModalType("success")
        setShowModal(true)

        // Store auth token if provided
        if (response.data.token) {
          // Assuming you're using cookies for auth
          document.cookie = `auth=${response.data.token}; path=/`
        }

        setTimeout(() => {
          navigate("/profile") // or wherever you want to redirect after login
        }, 2000)
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setModalMessage("Invalid email or password. Please try again.")
        setModalType("error")
      } else if (error.response && error.response.status === 403) {
        setModalMessage("Please verify your email before logging in.")
        setModalType("error")
      } else {
        setModalMessage("An error occurred while logging in. Please try again.")
        setModalType("error")
      }
      setShowModal(true)
    }

    setFormLoading(false)
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error("Please fix the errors in the form")
      return
    }

    setFormLoading(true)

    const { email, password, gymName, gymDescription, gymLocation } = formData

    const registrationData = {
      email,
      password,
      name: gymName,
      description: gymDescription,
      addressLine1: gymLocation.addressLine1,
      addressLine2: gymLocation.addressLine2,
      pinCode: gymLocation.pinCode,
      city: gymLocation.city,
      state: gymLocation.state,
      country: gymLocation.country,
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/register`, registrationData)

      if (response.status === 200 || response.status === 201) {
        setModalMessage("Registration successful! Please check your email to verify your account.")
        setModalType("success")
        setShowModal(true)
      }
    } catch (error) {
      if (error.response && error.response.status === 409) {
        setModalMessage("This email is already registered. Please log in instead.")
        setModalType("error")
      } else {
        setModalMessage("An error occurred while registering. Please try again.")
        setModalType("error")
      }
      setShowModal(true)
    }

    setFormLoading(false)
  }

  // Handle Email Verification from URL Token
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const token = queryParams.get("token")

    if (token) {
      verifyEmail(token)
    }
  }, [location.search])

  const verifyEmail = async (token) => {
    setLoading(true)
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/verify-email`, { token })

      if (response.status === 200) {
        setModalMessage("Email verified successfully! Redirecting to login...")
        setModalType("success")
        setShowModal(true)

        setTimeout(() => {
          navigate("/login")
        }, 3000)
      }
    } catch (error) {
      setModalMessage("Email verification failed. Please try again.")
      setModalType("error")
      setShowModal(true)
    }
    setLoading(false)
  }

  const closeModal = () => {
    setShowModal(false)
    if (modalType === "success" && modalMessage.includes("Registration successful")) {
      navigate("/login")
    }
  }

  if (loading) {
    return (
      <div className="loading-container">
        <nav className="rp-nav">
          <div className="rp-nav-inner">
            <Link to="/" className="rp-logo">
              <img src={Logo} alt="Yupluck" />
              <span>Yupluck</span>
            </Link>
          </div>
        </nav>
        <div className="loading-content">
          <Loader className="loading-spinner" />
          <p>Verifying your email...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="gym-registration-page">
      {/* Minimal nav */}
      <nav className="rp-nav">
        <div className="rp-nav-inner">
          <Link to="/" className="rp-logo">
            <img src={Logo} alt="Yupluck" />
            <span>Yupluck</span>
          </Link>
          <Link to="/login" className="rp-nav-link">
            Already have an account? <strong style={{ color: "#10b981" }}>Sign in →</strong>
          </Link>
        </div>
      </nav>

      <div className="registration-container">
        <div className="registration-content">
          {/* Left brand panel */}
          <div className="hero-section">
            <div className="hero-content">
              <h1>List your gym. Grow your business.</h1>
              <p>Join hundreds of gym owners already using Yupluck to manage members and grow revenue — completely free.</p>
              <div className="features-list">
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>Member &amp; equipment management</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>Booking &amp; scheduling system</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>Payment tracking &amp; analytics</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>QR code member check-ins</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span>1 Lakh+ users on the Yupluck app</span>
                </div>
              </div>
              <div className="hero-trust">
                <div className="hero-trust-title">100% Free — Always</div>
                <p>No credit card needed. No subscription fee. Get started in under 5 minutes.</p>
              </div>
            </div>
          </div>

          {/* Right side - Registration form */}
          <div className="form-section">
            <div className="form-container">
              <div className="form-header">
                <div className="auth-toggle">
                  <button
                    type="button"
                    className={`toggle-btn ${!isLoginMode ? 'active' : ''}`}
                    onClick={() => {
                      setIsLoginMode(false)
                      setFieldErrors({})
                    }}
                  >
                    Register
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${isLoginMode ? 'active' : ''}`}
                    onClick={() => {
                      setIsLoginMode(true)
                      setFieldErrors({})
                    }}
                  >
                    Login
                  </button>
                </div>
                <h2>{isLoginMode ? 'Welcome Back' : 'Register Your Gym'}</h2>
                <p>{isLoginMode ? 'Sign in to your gym management account' : 'Get started with your gym management journey'}</p>
              </div>

              {isLoginMode ? (
                // Login Form
                <form onSubmit={handleLogin} className="registration-form">
                  <div className="section">
                    <h3 className="section-title">
                      <User className="section-icon" />
                      Sign In
                    </h3>

                    <div className="form-group">
                      <label htmlFor="loginEmail">Email Address</label>
                      <div className="input-wrapper">
                        <Mail className="input-icon" />
                        <input
                          type="email"
                          id="loginEmail"
                          name="email"
                          value={loginData.email}
                          onChange={handleLoginChange}
                          className={fieldErrors.email ? "error" : ""}
                          placeholder="Enter your email"
                        />
                      </div>
                      {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="loginPassword">Password</label>
                      <div className="input-wrapper">
                        <Lock className="input-icon" />
                        <input
                          type="password"
                          id="loginPassword"
                          name="password"
                          value={loginData.password}
                          onChange={handleLoginChange}
                          className={fieldErrors.password ? "error" : ""}
                          placeholder="Enter your password"
                        />
                      </div>
                      {fieldErrors.password && <span className="error-message">{fieldErrors.password}</span>}
                    </div>

                    <div className="forgot-password">
                    <button
                  type="button"
                  className="forgot-password"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot password?
                </button>
                    </div>
                  </div>

                  <button type="submit" className="submit-button" disabled={formLoading}>
                    {formLoading ? (
                      <>
                        <Loader className="button-spinner" />
                        Signing In...
                      </>
                    ) : (
                      <>
                        <User className="button-icon" />
                        Sign In
                      </>
                    )}
                  </button>
                </form>
              ) : (
                // Registration Form (existing form code)
                <form onSubmit={handleSubmit} className="registration-form">
                  {/* Basic Information */}
                  <div className="section">
                    <h3 className="section-title">
                      <User className="section-icon" />
                      Basic Information
                    </h3>

                    <div className="form-group">
                      <label htmlFor="email">Email Address</label>
                      <div className="input-wrapper">
                        <Mail className="input-icon" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={fieldErrors.email ? "error" : ""}
                          placeholder="Enter your email"
                        />
                      </div>
                      {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <div className="input-wrapper">
                        <Lock className="input-icon" />
                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={fieldErrors.password ? "error" : ""}
                          placeholder="Create a password"
                        />
                      </div>
                      {fieldErrors.password && <span className="error-message">{fieldErrors.password}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="gymName">Gym Name</label>
                      <div className="input-wrapper">
                        <Building className="input-icon" />
                        <input
                          type="text"
                          id="gymName"
                          name="gymName"
                          value={formData.gymName}
                          onChange={handleInputChange}
                          className={fieldErrors.gymName ? "error" : ""}
                          placeholder="Enter your gym name"
                        />
                      </div>
                      {fieldErrors.gymName && <span className="error-message">{fieldErrors.gymName}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="gymDescription">Gym Description</label>
                      <div className="input-wrapper">
                        <FileText className="input-icon" />
                        <textarea
                          id="gymDescription"
                          name="gymDescription"
                          rows="3"
                          value={formData.gymDescription}
                          onChange={handleInputChange}
                          className={fieldErrors.gymDescription ? "error" : ""}
                          placeholder="Describe your gym and its facilities"
                        />
                      </div>
                      {fieldErrors.gymDescription && <span className="error-message">{fieldErrors.gymDescription}</span>}
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="section">
                    <h3 className="section-title">
                      <MapPin className="section-icon" />
                      Gym Location
                    </h3>

                    <div className="form-group">
                      <label htmlFor="addressLine1">Address Line 1</label>
                      <div className="input-wrapper">
                        <MapPin className="input-icon" />
                        <input
                          type="text"
                          id="addressLine1"
                          name="addressLine1"
                          value={formData.gymLocation.addressLine1}
                          onChange={handleGymLocationChange}
                          className={fieldErrors.addressLine1 ? "error" : ""}
                          placeholder="Street address"
                        />
                      </div>
                      {fieldErrors.addressLine1 && <span className="error-message">{fieldErrors.addressLine1}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="addressLine2">Address Line 2 (Optional)</label>
                      <div className="input-wrapper">
                        <MapPin className="input-icon" />
                        <input
                          type="text"
                          id="addressLine2"
                          name="addressLine2"
                          value={formData.gymLocation.addressLine2}
                          onChange={handleGymLocationChange}
                          placeholder="Apartment, suite, etc."
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="pinCode">Pin Code</label>
                        <div className="input-wrapper">
                          <Hash className="input-icon" />
                          <input
                            type="text"
                            id="pinCode"
                            name="pinCode"
                            value={formData.gymLocation.pinCode}
                            onChange={handleGymLocationChange}
                            className={fieldErrors.pinCode ? "error" : ""}
                            placeholder="000000"
                            maxLength="6"
                          />
                        </div>
                        {fieldErrors.pinCode && <span className="error-message">{fieldErrors.pinCode}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="country">Country</label>
                        <div className="input-wrapper">
                          <Globe className="input-icon" />
                          <input
                            type="text"
                            id="country"
                            name="country"
                            value={formData.gymLocation.country}
                            readOnly
                            className="readonly"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="state">State</label>
                        <div className="input-wrapper">
                          <MapPin className="input-icon" />
                          <select
                            id="state"
                            name="state"
                            value={formData.gymLocation.state}
                            onChange={handleGymLocationChange}
                            className={fieldErrors.state ? "error" : ""}
                          >
                            <option value="">Select State</option>
                            {states.map((state) => (
                              <option key={state.name} value={state.name}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {fieldErrors.state && <span className="error-message">{fieldErrors.state}</span>}
                      </div>

                      <div className="form-group">
                        <label htmlFor="city">City</label>
                        <div className="input-wrapper">
                          <MapPin className="input-icon" />
                          <select
                            id="city"
                            name="city"
                            value={formData.gymLocation.city}
                            onChange={handleGymLocationChange}
                            className={fieldErrors.city ? "error" : ""}
                            disabled={!formData.gymLocation.state}
                          >
                            <option value="">Select City</option>
                            {cities.map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                          </select>
                        </div>
                        {fieldErrors.city && <span className="error-message">{fieldErrors.city}</span>}
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="submit-button" disabled={formLoading}>
                    {formLoading ? (
                      <>
                        <Loader className="button-spinner" />
                        Registering...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="button-icon" />
                        Register Gym
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-header ${modalType}`}>
              {modalType === "success" && <CheckCircle className="modal-icon" />}
              {modalType === "error" && <AlertCircle className="modal-icon" />}
              {modalType === "info" && <AlertCircle className="modal-icon" />}
            </div>
            <div className="modal-body">
              <p>{modalMessage}</p>
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="modal-button">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
    </div>
  )
}

export default GymRegistration
